/**
 * ATELIER D'EXERCICES — méthodologie de chaque exercice (module PUR).
 *
 * Source unique, affichée à l'étudiant ET injectée dans les consignes des
 * agents. Établie le 03/10/2026 en recoupant des guides de facultés de droit
 * françaises (Paris 1-IDAI, Paris Nanterre, Toulouse Capitole, Montpellier,
 * Strasbourg, Bordeaux), la documentation de la Cour de cassation, du Conseil
 * d'État et du Conseil constitutionnel, et les informations officielles sur
 * le CRFPA. Les points sur lesquels les facultés divergent sont tranchés par
 * un choix par défaut explicite (conclusion, forme de la problématique…), que
 * les consignes de l'enseignant emportent toujours.
 */
import type { EtapeId, TypeExercice } from "./types.ts";

export interface EtapeAffichee {
  titre: string;
  /** Ce que fait l'agent, en une phrase. */
  role: string;
  /** Ce que la méthode exige à ce stade : affiché à l'étudiant. */
  methode: string;
}

export interface CritereBareme {
  id: string;
  libelle: string;
  max: number;
  /** Ce que le correcteur vérifie pour accorder les points. */
  attendus: string;
}

export interface Champ {
  libelle: string;
  aide: string;
  exemple?: string;
}

export interface MethodeExercice {
  type: TypeExercice;
  libelle: string;
  /** Une phrase de présentation. */
  resume: string;
  /** Format de l'épreuve de référence. */
  epreuve: string;
  /** Repères de longueur de la copie modèle, en mots. */
  longueur: { min: number; max: number };
  enBref: string[];
  erreurs: string[];
  excellence: string[];
  etapes: Partial<Record<EtapeId, EtapeAffichee>>;
  bareme: CritereBareme[];
  champs: { sujet?: Champ; texte?: Champ; documents?: Champ };
}

// ───────────────────────── Étapes communes ─────────────────────────

const RECHERCHE: EtapeAffichee = {
  titre: "Recherche des sources",
  role: "L'agent interroge la base officielle (Légifrance), la doctrine en ligne et la jurisprudence déjà vérifiée.",
  methode:
    "Rassembler les textes en vigueur, la jurisprudence de principe et les débats doctrinaux avant de construire quoi que ce soit : une copie ne vaut que par l'exactitude de ses fondements.",
};

const CHOIX_PLAN: EtapeAffichee = {
  titre: "Choix du plan par un examinateur",
  role: "Un second modèle, d'une autre famille, joue l'examinateur : il compare les plans, retient le meilleur et l'affine.",
  methode:
    "Un bon plan répond à la problématique dans ses deux parties, n'est ni descriptif ni « catalogue », et porte des titres nominaux, sans verbe ni point d'interrogation, de longueur et de construction voisines.",
};

const CORRECTION: EtapeAffichee = {
  titre: "Correction selon le barème",
  role: "L'examinateur note la copie critère par critère et relève chaque défaut avec la correction à apporter.",
  methode: "Relire comme un correcteur de faculté : respect de la méthode, exactitude juridique, qualité de la démonstration, forme.",
};

const REVISION: EtapeAffichee = {
  titre: "Révision ciblée",
  role: "Le rédacteur reprend uniquement les passages signalés, sans toucher à ce qui est juste.",
  methode: "Corriger chaque défaut relevé, sans introduire de nouvelle référence qui n'aurait pas été vérifiée.",
};

const VERIFICATION: EtapeAffichee = {
  titre: "Vérification des références",
  role: "Chaque article et chaque décision cités sont confrontés aux sources officielles ; ce qui est faux est retiré.",
  methode: "Une absence de référence vaut toujours mieux qu'une référence fausse : un numéro de pourvoi inventé coûte bien plus qu'il ne rapporte.",
};

const EVALUATION: EtapeAffichee = {
  titre: "Évaluation finale",
  role: "L'examinateur note la copie définitive et explique ce qui fait sa valeur et ce qui pourrait encore l'améliorer.",
  methode: "La note est indicative : elle reproduit la grille d'un correcteur exigeant, pas celle de votre chargé de travaux dirigés.",
};

// ───────────────────────── Fiche d'arrêt ─────────────────────────

const FICHE_ARRET: MethodeExercice = {
  type: "fiche_arret",
  libelle: "Fiche d'arrêt",
  resume: "Décomposer fidèlement une décision : qui a dit quoi, sur quelle question, avec quelle réponse.",
  epreuve: "Exercice de travaux dirigés (L1-L2) et première étape obligatoire de tout commentaire.",
  longueur: { min: 300, max: 900 },
  enBref: [
    "Lire la décision au moins deux fois, la seconde fois en repérant qui parle : les juges du fond, le pourvoi, la Cour.",
    "Identifier la décision : juridiction, formation, date, numéro, publication, sens (cassation, rejet…).",
    "Faits : chronologiques, qualifiés juridiquement, neutres, tirés de « Selon l'arrêt attaqué » et arrêtés avant la saisine du premier juge.",
    "Procédure : qui a saisi quel juge, qui a fait appel, qui s'est pourvu — sans jamais rien inventer.",
    "Thèses : en cas de cassation, la thèse rejetée est celle de la cour d'appel ; en cas de rejet, celle du pourvoi.",
    "Problème de droit : la question posée par l'argumentation rejetée, abstraite, fermée, sans nom propre.",
    "Solution : dispositif et motifs, avec le visa, le chapeau et l'intensité du contrôle exercé.",
  ],
  erreurs: [
    "Inventer un fait ou une étape de procédure absents de la décision.",
    "Laisser les noms des parties au lieu de leur qualité juridique (le vendeur, l'employeur, la victime).",
    "Lire le moyen comme si c'était la réponse de la Cour.",
    "Construire le problème de droit à partir de la solution.",
    "Tronquer la solution en oubliant le visa ou le chapeau.",
    "Glisser une appréciation critique dans la fiche.",
  ],
  excellence: [
    "Exploiter les détails : arrêt confirmatif ou infirmatif, motifs propres ou adoptés, motif surabondant, substitution de motifs.",
    "Qualifier exactement l'intensité du contrôle (« a pu en déduire », « à bon droit », « souverainement »).",
    "Formuler un problème de droit auquel les deux thèses répondent en sens contraire.",
  ],
  etapes: {
    analyse: {
      titre: "Lecture et décomposition de la décision",
      role: "L'agent identifie la décision, sépare les faits, la procédure, les thèses et la réponse de la juridiction, sans rien ajouter.",
      methode: "Tout doit se trouver dans la décision : ce qui n'y figure pas est signalé comme absent, jamais reconstitué.",
    },
    redaction: {
      titre: "Rédaction de la fiche",
      role: "L'agent rédige chaque rubrique dans l'ordre : identification, faits, procédure, thèses, problème de droit, solution.",
      methode: "Phrases complètes, faits au passé, parties désignées par leur qualité juridique, aucune critique.",
    },
    correction: {
      titre: "Contrôle de fidélité",
      role: "L'examinateur confronte chaque affirmation de la fiche au texte de la décision.",
      methode: "Chaque élément doit pouvoir être retrouvé dans la décision ; la thèse rejetée doit correspondre au sens de l'arrêt.",
    },
    revision: REVISION,
    evaluation: EVALUATION,
  },
  bareme: [
    { id: "identification", libelle: "Identification de la décision", max: 2, attendus: "Juridiction, formation, date, numéro, publication et sens de la décision exacts." },
    { id: "faits", libelle: "Faits", max: 3, attendus: "Faits pertinents, qualifiés, chronologiques, neutres, sans invention ni noms propres." },
    { id: "procedure", libelle: "Procédure", max: 3, attendus: "Parcours procédural exact et complet, sans étape inventée." },
    { id: "theses", libelle: "Thèses en présence", max: 4, attendus: "Thèse rejetée correctement identifiée selon qu'il s'agit d'une cassation ou d'un rejet ; moyens et motifs non confondus." },
    { id: "probleme", libelle: "Problème de droit", max: 4, attendus: "Question abstraite, fermée, tirée de l'argumentation rejetée, à laquelle les deux thèses répondent en sens contraire." },
    { id: "solution", libelle: "Solution", max: 3, attendus: "Dispositif et motifs, visa, chapeau, intensité du contrôle." },
    { id: "forme", libelle: "Forme et vocabulaire", max: 1, attendus: "Vocabulaire juridique exact (moyens ≠ motifs), orthographe irréprochable." },
  ],
  champs: {
    texte: { libelle: "Texte intégral de la décision", aide: "Copiez la décision depuis Légifrance ou votre plaquette de TD : la fiche se fait sur le texte, jamais sur la seule référence." },
  },
};

// ───────────────────────── Commentaire d'arrêt ─────────────────────────

const COMMENTAIRE_ARRET: MethodeExercice = {
  type: "commentaire_arret",
  libelle: "Commentaire d'arrêt",
  resume: "Expliquer et apprécier une décision : son sens, sa valeur, sa portée — sans paraphrase ni dissertation.",
  epreuve: "Épreuve de 3 heures (L1 à M1 ; possible au CRFPA).",
  longueur: { min: 1_800, max: 3_200 },
  enBref: [
    "Lire la décision trois fois en pesant chaque mot : une incise, une négation, un adverbe se commentent.",
    "Faire la fiche d'arrêt complète, puis qualifier techniquement la décision : cas d'ouverture, intensité du contrôle, visa, publication, motivation enrichie.",
    "Remplir la grille contexte, sens, valeur, portée — c'est une grille d'analyse, pas un plan.",
    "Vérifier l'application de la loi dans le temps et la renumérotation des textes (réforme de 2016 du droit des contrats).",
    "Construire un plan en deux parties tiré de la décision elle-même (principe et limite, qualification et conséquence, deux questions…), le cœur de l'analyse en I.B et II.A.",
    "Introduction : présentation, faits, procédure, thèses, problème de droit, solution, intérêt de la décision, annonce du I et du II.",
    "Dans chaque sous-partie : citer la décision, la replacer dans son contexte, l'apprécier.",
  ],
  erreurs: [
    "Paraphraser la décision ou, à l'inverse, disserter en la prenant pour prétexte.",
    "Plan descriptif qui suit l'ordre de l'arrêt, ou plan « I. Sens — II. Valeur et portée ».",
    "Titres comportant un verbe, un point d'interrogation ou le nom d'une juridiction.",
    "Commenter le pourvoi plutôt que la décision, ou commenter le dispositif.",
    "Annoncer qu'il s'agit d'un « arrêt de principe » sans l'établir (un arrêt simplement diffusé ne l'est pas).",
    "Interpréter un rejet comme une approbation de toute la décision attaquée.",
    "Commenter un arrêt ancien comme s'il appliquait le droit actuel.",
  ],
  excellence: [
    "Une introduction qui permet de comprendre la décision sans la lire.",
    "Un problème de droit qui révèle ce que la décision ne dit pas.",
    "Une lecture exacte du contrôle exercé et des formules de la juridiction.",
    "Des titres qui répondent à la problématique, une appréciation en droit et en opportunité, une portée datée et mise à jour.",
  ],
  etapes: {
    analyse: {
      titre: "Fiche d'arrêt et qualification technique",
      role: "L'agent décompose la décision (faits, procédure, thèses, problème, solution) et remplit la grille contexte, sens, valeur, portée.",
      methode: "Le problème de droit se construit à partir de l'argumentation que la juridiction rejette, jamais à partir de sa réponse.",
    },
    recherche: RECHERCHE,
    plans: {
      titre: "Trois plans candidats",
      role: "L'agent propose trois plans tirés de la structure même de la décision, chacun avec sa logique.",
      methode: "Le plan se déduit de la décision : deux questions distinctes, qualification puis conséquence, principe puis limite… jamais un plan prêt-à-porter.",
    },
    choix_plan: CHOIX_PLAN,
    redaction: {
      titre: "Rédaction",
      role: "L'introduction et les deux parties sont rédigées par des agents qui suivent le plan retenu et le dossier de sources.",
      methode: "Introduction fondue en prose (aucune rubrique titrée), chapeaux sous I et II, transitions, décision citée dans chaque sous-partie.",
    },
    correction: CORRECTION,
    revision: REVISION,
    verification: VERIFICATION,
    evaluation: EVALUATION,
  },
  bareme: [
    { id: "introduction", libelle: "Introduction et fiche d'arrêt", max: 5, attendus: "Présentation exacte, faits et procédure fidèles, thèses identifiées, solution complète, intérêt de la décision, annonce du I et du II." },
    { id: "probleme", libelle: "Problème de droit", max: 2, attendus: "Tiré de l'argumentation rejetée, abstrait, précis." },
    { id: "plan", libelle: "Plan et titres", max: 3, attendus: "Plan tiré de la décision, non descriptif ; titres nominaux, qualifiés, parallèles, annoncés à l'identique ; chapeaux et transitions." },
    { id: "sens", libelle: "Explication de la décision (sens)", max: 4, attendus: "Raisonnement de la juridiction restitué et expliqué, contrôle exercé, contexte juridique, décision citée dans chaque sous-partie." },
    { id: "valeur_portee", libelle: "Appréciation (valeur et portée)", max: 4, attendus: "Critique argumentée en droit et en opportunité, solutions alternatives, place dans la jurisprudence, évolutions postérieures." },
    { id: "forme", libelle: "Forme", max: 2, attendus: "Style impersonnel, vocabulaire juridique exact, orthographe irréprochable, aucune paraphrase." },
  ],
  champs: {
    texte: { libelle: "Texte intégral de la décision", aide: "Copiez la décision complète (Légifrance, plaquette de TD). Le commentaire ne se fait jamais à partir de la seule référence." },
    sujet: { libelle: "Consigne (facultatif)", aide: "Par exemple : « Commentez l'arrêt suivant » ou les questions qui accompagnent la décision." },
  },
};

// ───────────────────────── Dissertation ─────────────────────────

const DISSERTATION: MethodeExercice = {
  type: "dissertation",
  libelle: "Dissertation juridique",
  resume: "Démontrer, en deux parties, une réponse argumentée à la problématique que pose le sujet.",
  epreuve: "Épreuve de 3 heures (L1 à M1 ; possible au CRFPA).",
  longueur: { min: 1_800, max: 3_200 },
  enBref: [
    "Recopier le sujet sans en changer un mot, puis définir chaque terme, y compris les « petits mots » (« et », articles, ponctuation).",
    "Délimiter le sujet dans la matière, l'espace et le temps, et justifier les exclusions.",
    "Recenser toutes les connaissances, les classer, puis en dégager les intérêts du sujet et ses tensions.",
    "Formuler une problématique qui n'est jamais le sujet reformulé et qui couvre tout le sujet.",
    "Construire un plan en deux parties qui répondent chacune à la problématique ; pour un sujet « X et Y », jamais « I. X — II. Y ».",
    "Introduction : accroche, définitions, délimitation, contexte, intérêts, problématique, annonce du I et du II.",
    "Chaque paragraphe : une idée, son argumentation, une illustration (texte, décision), une mise en perspective.",
  ],
  erreurs: [
    "Réciter le cours au lieu de répondre au sujet.",
    "Traiter le sujet partiellement ou en sortir.",
    "Faire un catalogue de connaissances sans fil directeur.",
    "Annoncer le plan de manière scolaire (« dans une première partie, nous verrons… »).",
    "Parties déséquilibrées ou plan qui ne répond pas à la problématique.",
    "Plan « oui / non » pour un sujet posé en question.",
  ],
  excellence: [
    "Des définitions exactes qui servent réellement à délimiter le sujet.",
    "Une problématique personnelle, porteuse d'un mouvement.",
    "Un plan d'idées dont les titres reprennent les termes du sujet et répondent à la problématique.",
    "Des sources variées et exactes, une perspective historique, pratique ou critique.",
  ],
  etapes: {
    analyse: {
      titre: "Analyse du sujet",
      role: "L'agent définit chaque terme, délimite le sujet, identifie son type, ses intérêts et ses tensions, et ses pièges.",
      methode: "Tout part des définitions : elles délimitent le sujet et font apparaître la tension qui deviendra la problématique.",
    },
    recherche: RECHERCHE,
    plans: {
      titre: "Problématiques et plans candidats",
      role: "L'agent propose trois couples problématique et plan, de logiques différentes (analytique, dialectique…).",
      methode: "Comparer plusieurs plans avant d'en retenir un : c'est l'étape qui fait la note.",
    },
    choix_plan: CHOIX_PLAN,
    redaction: {
      titre: "Rédaction",
      role: "L'introduction et les deux parties sont rédigées par des agents qui suivent le plan retenu et le dossier de sources.",
      methode: "Introduction d'environ un quart de la copie, chapeaux, transitions, une idée par paragraphe, pas de conclusion sauf demande.",
    },
    correction: CORRECTION,
    revision: REVISION,
    verification: VERIFICATION,
    evaluation: EVALUATION,
  },
  bareme: [
    { id: "introduction", libelle: "Introduction", max: 4, attendus: "Accroche pertinente, définitions exactes, délimitation justifiée, contexte, intérêts, annonce du I et du II." },
    { id: "problematique", libelle: "Problématique", max: 3, attendus: "Ne reformule pas le sujet, couvre tout le sujet, traduit une tension et un mouvement." },
    { id: "plan", libelle: "Plan et titres", max: 3, attendus: "Deux parties qui répondent chacune à la problématique ; titres nominaux reprenant les termes du sujet ; chapeaux et transitions." },
    { id: "argumentation", libelle: "Argumentation et fondements", max: 7, attendus: "Démonstration progressive, une idée par paragraphe, textes et décisions exacts et expliqués, discussion critique." },
    { id: "forme", libelle: "Forme", max: 3, attendus: "Style impersonnel, vocabulaire juridique exact, orthographe irréprochable, équilibre." },
  ],
  champs: {
    sujet: { libelle: "Sujet de la dissertation", aide: "Recopiez le sujet exactement, sans le reformuler.", exemple: "La bonne foi dans l'exécution du contrat" },
  },
};

// ───────────────────────── Plan détaillé ─────────────────────────

const PLAN_DETAILLE: MethodeExercice = {
  type: "plan_detaille",
  libelle: "Plan détaillé",
  resume: "Une introduction entièrement rédigée et un plan détaillé jusqu'aux subdivisions, avec les idées et les références de chacune.",
  epreuve: "Exercice de travaux dirigés et format d'examen (souvent en 1 h 30 ou en 3 h).",
  longueur: { min: 1_100, max: 2_000 },
  enBref: [
    "Même travail préparatoire que la dissertation : définitions, délimitation, intérêts, tensions, problématique.",
    "Introduction rédigée intégralement, dans l'ordre : accroche, définitions, délimitation, contexte, intérêts, problématique, annonce du I et du II.",
    "Plan en deux parties et deux sous-parties, chaque sous-partie divisée en 1 et 2.",
    "Titres nominaux à tous les niveaux, qui répondent à la problématique.",
    "Sous chaque subdivision : l'idée en une ou deux phrases, puis les textes et décisions qui la fondent.",
    "Chapeaux et transitions en une phrase chacun.",
  ],
  erreurs: [
    "Une introduction bâclée : elle compte autant que le plan.",
    "Des titres-catalogues (« Les conditions », « Les effets ») sans lien avec la problématique.",
    "Des subdivisions vides de contenu ou sans fondement juridique.",
    "Un plan qui ne répond qu'à une partie du sujet.",
  ],
  excellence: [
    "Des titres qui, lus seuls, forment une démonstration.",
    "Des subdivisions équilibrées, chacune portant une idée précise et ses références exactes.",
  ],
  etapes: {
    analyse: { titre: "Analyse du sujet", role: "L'agent définit chaque terme, délimite le sujet et fait apparaître ses tensions.", methode: "Tout part des définitions : elles délimitent le sujet et révèlent la problématique." },
    recherche: RECHERCHE,
    plans: { titre: "Plans candidats", role: "L'agent propose trois plans de logiques différentes.", methode: "Comparer plusieurs plans avant d'en retenir un : c'est le cœur de l'exercice." },
    choix_plan: CHOIX_PLAN,
    redaction: { titre: "Rédaction", role: "L'introduction est rédigée en entier ; le plan est détaillé jusqu'aux subdivisions 1 et 2.", methode: "Sous chaque subdivision, l'idée et ses fondements, en phrases brèves." },
    correction: CORRECTION,
    revision: REVISION,
    verification: VERIFICATION,
    evaluation: EVALUATION,
  },
  bareme: [
    { id: "introduction", libelle: "Introduction rédigée", max: 5, attendus: "Toutes les étapes, dans l'ordre ; définitions et délimitation exactes ; annonce du I et du II." },
    { id: "problematique", libelle: "Problématique", max: 3, attendus: "Ne reformule pas le sujet ; traduit sa tension." },
    { id: "plan", libelle: "Plan et titres", max: 6, attendus: "Deux parties qui répondent à la problématique ; subdivisions 1 et 2 ; titres nominaux, démonstratifs et équilibrés." },
    { id: "contenu", libelle: "Contenu des subdivisions", max: 4, attendus: "Idées précises, fondements exacts (textes, décisions), sans remplissage." },
    { id: "forme", libelle: "Forme", max: 2, attendus: "Vocabulaire juridique exact, orthographe irréprochable." },
  ],
  champs: {
    sujet: { libelle: "Sujet", aide: "Recopiez le sujet exactement, sans le reformuler.", exemple: "Le juge et le contrat" },
  },
};

// ───────────────────────── Commentaire de texte ─────────────────────────

const COMMENTAIRE_TEXTE: MethodeExercice = {
  type: "commentaire_texte",
  libelle: "Commentaire de texte ou d'article",
  resume: "Expliquer et discuter un texte (article de code, de la Constitution, loi, extrait de doctrine) à partir de ses propres termes.",
  epreuve: "Épreuve de 3 heures (L1 à M1).",
  longueur: { min: 1_600, max: 3_000 },
  enBref: [
    "Identifier le texte : nature, auteur, date, source, place dans le code ou dans l'ouvrage dont il est extrait.",
    "Analyser sa structure (alinéas, mouvements, connecteurs comme « toutefois », « sauf », « à peine de ») et peser chaque mot (« peut » ou « doit », « tout », « de plein droit »).",
    "Retracer sa genèse (travaux préparatoires, réformes, jurisprudence qu'il codifie) et son interprétation par la jurisprudence et la doctrine.",
    "Introduction : accroche, présentation du texte, historique, objet et définitions, intérêt, problématique, annonce du I et du II.",
    "Plan tiré du texte : sa structure interne (principe et exception, conditions et effets) ou, plus audacieux, analyse puis discussion.",
    "Dans chaque sous-partie : citer les termes exacts du texte, les replacer dans leur contexte, les discuter.",
    "Test décisif : une partie qu'on pourrait écrire sans le texte est une dissertation.",
  ],
  erreurs: [
    "Disserter sur le thème du texte au lieu de commenter le texte : c'est la faute cardinale.",
    "Paraphraser le texte ou réciter le cours.",
    "Oublier un alinéa ou un terme clé.",
    "Commenter une version abrogée sans le signaler.",
    "Titres génériques (« Le sens », « La portée ») ou phrases coupées entre A et B.",
  ],
  excellence: [
    "Des titres qui, lus seuls, résument le commentaire.",
    "Chaque mot important expliqué puis discuté.",
    "La jurisprudence et la doctrine mises au service du texte, une portée actualisée.",
  ],
  etapes: {
    analyse: {
      titre: "Analyse du texte",
      role: "L'agent identifie le texte, découpe sa structure, définit ses termes clés et retrace sa genèse, son interprétation et sa portée.",
      methode: "Chaque mot compte : « peut » n'est pas « doit », « tout » n'est pas « certains ».",
    },
    recherche: RECHERCHE,
    plans: {
      titre: "Trois plans tirés du texte",
      role: "L'agent propose trois plans ancrés dans les termes du texte.",
      methode: "Le plan suit la logique du texte ; il ne traite jamais le thème en général.",
    },
    choix_plan: CHOIX_PLAN,
    redaction: {
      titre: "Rédaction",
      role: "L'introduction et les deux parties sont rédigées en citant le texte dans chaque sous-partie.",
      methode: "Citation courte, explication, mise en relation avec les autres textes et la jurisprudence, appréciation.",
    },
    correction: CORRECTION,
    revision: REVISION,
    verification: VERIFICATION,
    evaluation: EVALUATION,
  },
  bareme: [
    { id: "introduction", libelle: "Introduction", max: 5, attendus: "Présentation exacte du texte, historique, objet, définitions, intérêt, problématique, annonce du I et du II." },
    { id: "plan", libelle: "Plan et titres", max: 4, attendus: "Plan tiré du texte et le couvrant en entier ; titres nominaux ; chapeaux et transitions." },
    { id: "sens", libelle: "Analyse du sens", max: 5, attendus: "Chaque terme clé expliqué, structure du texte restituée, citations exactes dans chaque sous-partie." },
    { id: "valeur_portee", libelle: "Valeur et portée", max: 4, attendus: "Interprétation jurisprudentielle et doctrinale, critique argumentée, portée actualisée." },
    { id: "forme", libelle: "Expression", max: 2, attendus: "Style impersonnel, vocabulaire exact, orthographe irréprochable." },
  ],
  champs: {
    texte: { libelle: "Texte à commenter", aide: "Collez le texte exact (article, alinéas, extrait) : le commentaire se fait sur ses termes." },
    sujet: { libelle: "Consigne (facultatif)", aide: "Par exemple : « Commentez l'article 1240 du Code civil », ou les questions qui accompagnent le texte." },
  },
};

// ───────────────────────── Cas pratique ─────────────────────────

const CAS_PRATIQUE: MethodeExercice = {
  type: "cas_pratique",
  libelle: "Cas pratique",
  resume: "Résoudre une situation concrète : qualifier les faits, poser les questions de droit, raisonner par syllogisme, trancher.",
  epreuve: "Épreuve de 3 heures (L1 à M2 ; base de la consultation au CRFPA).",
  longueur: { min: 1_400, max: 3_000 },
  enBref: [
    "Lire l'énoncé deux fois ; dresser la chronologie, la liste des personnes, des dates et des montants. Les dates signalent souvent la loi applicable ou une prescription.",
    "Qualifier juridiquement les faits utiles (« un adolescent de 14 ans » devient « un mineur ») sans jamais rien inventer.",
    "Identifier TOUS les problèmes et les ordonner : questions préalables d'abord (qualification, puis validité, puis effets ; en droit public, compétence, recevabilité, puis fond ; en droit pénal, l'auteur avant le complice).",
    "Formuler chaque question de droit en termes généraux, sans nom propre.",
    "Syllogisme : la règle (texte, définition, conditions, exceptions, effets, arrêts de principe), puis « En l'espèce » chaque condition confrontée aux faits, puis une conclusion nette.",
    "Fait incertain : raisonner par hypothèses (« à supposer que… »), puis trancher pour la plus plausible.",
    "Preuve, délais, prescription et juridiction compétente quand les faits le justifient ; une solution nette, même négative.",
  ],
  erreurs: [
    "Recopier les faits, ou en inventer.",
    "Poser des questions de fait au lieu de questions de droit.",
    "Réciter le cours au lieu de l'appliquer.",
    "Oublier une condition de la règle.",
    "Citer un arrêt avant le texte, ou présenter un arrêt d'espèce comme une règle générale.",
    "Ignorer les dates (loi applicable, prescription, délais).",
    "Développer les effets d'un mécanisme déjà écarté ; laisser des hypothèses sans trancher.",
  ],
  excellence: [
    "Chaque question reçoit une réponse explicite ; chaque condition est confrontée à un fait précis.",
    "Le principe et les exceptions sont traités ; les hypothèses sont justifiées puis tranchées.",
    "Aucun développement inutile ; une brève explication des actions écartées.",
  ],
  etapes: {
    analyse: {
      titre: "Analyse de l'énoncé",
      role: "L'agent dresse la chronologie, qualifie les faits, repère les dates et identifie tous les problèmes de droit.",
      methode: "Ne garder que les faits utiles, les traduire en catégories juridiques, ne rien inventer.",
    },
    recherche: RECHERCHE,
    plans: {
      titre: "Raisonnement juridique",
      role: "L'agent construit, pour chaque problème, la question de droit, les règles et leurs conditions, l'application aux faits et la conclusion.",
      methode: "Questions préalables d'abord ; une question de droit abstraite par problème ; chaque condition confrontée aux faits.",
    },
    choix_plan: {
      titre: "Contrôle du raisonnement par un examinateur",
      role: "Un second modèle, d'une autre famille, vérifie qu'aucun problème n'est oublié, que les règles sont complètes et que l'ordre est juste.",
      methode: "Une question manquée ne rapporte rien ; une condition oubliée fausse la solution.",
    },
    redaction: {
      titre: "Rédaction",
      role: "Chaque problème est rédigé par un agent, en parallèle, selon le syllogisme.",
      methode: "La règle, « En l'espèce », la conclusion : sans jamais intituler un paragraphe « majeure » ni « mineure ».",
    },
    correction: CORRECTION,
    revision: REVISION,
    verification: VERIFICATION,
    evaluation: EVALUATION,
  },
  bareme: [
    { id: "problemes", libelle: "Identification des problèmes et questions de droit", max: 4, attendus: "Tous les problèmes repérés et bien ordonnés ; questions de droit abstraites." },
    { id: "regles", libelle: "Règles de droit", max: 6, attendus: "Textes exacts, conditions complètes, exceptions, jurisprudence de principe bien située." },
    { id: "application", libelle: "Application aux faits", max: 6, attendus: "« En l'espèce » condition par condition, faits précis, hypothèses justifiées et tranchées, dates et délais pris en compte." },
    { id: "solutions", libelle: "Solutions", max: 2, attendus: "Réponse nette à chaque question, conséquences concrètes." },
    { id: "forme", libelle: "Forme", max: 2, attendus: "Structure claire, vocabulaire exact, orthographe irréprochable." },
  ],
  champs: {
    sujet: { libelle: "Énoncé du cas pratique", aide: "Collez l'énoncé complet : les faits et la ou les questions posées." },
  },
};

// ───────────────────────── Consultation ─────────────────────────

const CONSULTATION: MethodeExercice = {
  type: "consultation",
  libelle: "Consultation juridique",
  resume: "Conseiller une personne sur sa situation : réponses argumentées, procédure, risques et recommandations concrètes.",
  epreuve: "Épreuves du CRFPA (obligations 3 h, spécialité 3 h, procédure 2 h) ; exercice de master.",
  longueur: { min: 1_400, max: 3_000 },
  enBref: [
    "Même socle que le cas pratique (qualification, questions de droit, syllogisme), avec un conseil opérationnel en plus.",
    "N'examiner que la situation de celui qui consulte : son objectif guide toute la consultation.",
    "Pour chaque question : la règle, son application, la réponse, ses conditions et réserves, les risques.",
    "Préciser la procédure : juridiction compétente, délais, prescription courte ou proche.",
    "Recommander la solution la plus sûre après avoir pesé la preuve, le coût et la procédure.",
    "Terminer par une synthèse obligatoire : réponses courtes et recommandations classées (actions, pièces à réunir, délais, juridiction, voie amiable).",
  ],
  erreurs: [
    "Traiter la situation d'une personne qui ne consulte pas.",
    "Faire de la théorie ou un catalogue de solutions que le client ne cherche pas.",
    "Oublier les délais ou la juridiction compétente.",
    "Ne donner aucune recommandation finale, ou une recommandation vague.",
    "Ignorer les pièces mentionnées dans l'énoncé.",
  ],
  excellence: [
    "Une analyse tournée vers le résultat pour le client, sans surcharge théorique.",
    "Une procédure exacte et des délais calculés.",
    "Des recommandations hiérarchisées et immédiatement utilisables.",
  ],
  etapes: {
    analyse: {
      titre: "Analyse de la demande",
      role: "L'agent identifie le client, son objectif, les faits utiles, les pièces, les dates et les questions à résoudre.",
      methode: "Qui consulte, et que veut-il obtenir ? Toute la consultation en découle.",
    },
    recherche: RECHERCHE,
    plans: {
      titre: "Raisonnement et stratégie",
      role: "L'agent construit pour chaque question la règle, l'application, la réponse, les risques et le conseil.",
      methode: "Chaque réponse est utile au client : conditions, réserves, procédure et délais.",
    },
    choix_plan: {
      titre: "Contrôle par un examinateur",
      role: "Un second modèle vérifie qu'aucune question n'est oubliée, que les règles et les délais sont exacts et que les conseils servent le client.",
      methode: "Une consultation se juge à l'exactitude de ses réponses et à la qualité de ses recommandations.",
    },
    redaction: {
      titre: "Rédaction",
      role: "La consultation est rédigée question par question, puis close par la synthèse et les recommandations.",
      methode: "Objet, faits utiles, demande, analyse de chaque question, synthèse et recommandations.",
    },
    correction: CORRECTION,
    revision: REVISION,
    verification: VERIFICATION,
    evaluation: EVALUATION,
  },
  bareme: [
    { id: "demande", libelle: "Compréhension de la demande", max: 3, attendus: "Client et objectif identifiés ; faits qualifiés ; toutes les questions repérées." },
    { id: "regles", libelle: "Règles de droit", max: 5, attendus: "Textes exacts, conditions complètes, jurisprudence bien utilisée." },
    { id: "application", libelle: "Application et réponses", max: 5, attendus: "Application condition par condition, réponses nettes avec conditions et réserves." },
    { id: "procedure", libelle: "Procédure, délais et risques", max: 3, attendus: "Juridiction, délais, prescription, preuve et risques exactement identifiés." },
    { id: "conseils", libelle: "Conseils et recommandations", max: 3, attendus: "Synthèse finale, recommandations hiérarchisées et opérationnelles." },
    { id: "forme", libelle: "Forme", max: 1, attendus: "Structure claire, style professionnel, orthographe irréprochable." },
  ],
  champs: {
    sujet: { libelle: "Énoncé de la consultation", aide: "Collez l'énoncé complet : la situation du client, les pièces mentionnées et sa demande." },
  },
};

// ───────────────────────── Note de synthèse ─────────────────────────

const NOTE_SYNTHESE: MethodeExercice = {
  type: "note_synthese",
  libelle: "Note de synthèse",
  resume: "Restituer objectivement, en quatre pages au plus, l'essentiel d'un dossier documentaire, sans rien y ajouter.",
  epreuve: "CRFPA (5 h, coefficient 3), ENM et concours administratifs ; exercice de master 1.",
  longueur: { min: 1_000, max: 1_500 },
  enBref: [
    "Décortiquer le titre du sujet, puis feuilleter le dossier : nature, date et importance de chaque document.",
    "Lire chaque document avec un objectif et en faire une fiche : idées clés, apport, thèmes.",
    "Croiser thèmes et documents, regrouper en quatre blocs, en tirer un plan thématique en deux parties et deux sous-parties.",
    "Introduction brève (12 à 20 lignes) : accroche tirée du dossier, définitions et contexte, problématique, annonce du plan.",
    "Chaque idée est rattachée à ses documents : « (doc. 3) », « (doc. 2, 5 et 9) » ; chaque document est cité au moins une fois.",
    "Neutralité absolue : aucune connaissance extérieure, aucun avis, pas de conclusion ; quatre pages au plus (environ 1 300 mots).",
  ],
  erreurs: [
    "Résumer les documents l'un après l'autre au lieu de les croiser.",
    "Oublier un document.",
    "Ajouter des connaissances personnelles, même exactes, ou donner son avis.",
    "Dépasser quatre pages.",
    "Plan déséquilibré ou inadapté au sujet précis.",
  ],
  excellence: [
    "Une problématique qui exprime la tension centrale du dossier.",
    "Des phrases qui croisent plusieurs documents (convergences, divergences, évolution).",
    "Des sources hiérarchisées : textes et décisions avant commentaires, commentaires avant presse.",
  ],
  etapes: {
    analyse: {
      titre: "Dépouillement du dossier",
      role: "L'agent établit une fiche par document puis le tableau croisé des thèmes et des documents.",
      methode: "Lire chaque document avec un objectif ; relever ce qu'il apporte, pas ce qu'il dit à la marge.",
    },
    plans: {
      titre: "Trois plans thématiques",
      role: "L'agent propose trois plans en deux parties qui placent chaque document au moins une fois.",
      methode: "Jamais un plan document par document : chaque sous-partie croise plusieurs documents.",
    },
    choix_plan: CHOIX_PLAN,
    redaction: {
      titre: "Rédaction",
      role: "L'introduction et les deux parties sont rédigées à partir du seul dossier, en citant les documents.",
      methode: "Neutralité, aucune connaissance extérieure, « (doc. n) » à chaque idée, quatre pages au plus.",
    },
    correction: CORRECTION,
    revision: REVISION,
    evaluation: EVALUATION,
  },
  bareme: [
    { id: "introduction", libelle: "Introduction", max: 3, attendus: "Accroche tirée du dossier, définitions et contexte, problématique, annonce du plan ; 12 à 20 lignes." },
    { id: "plan", libelle: "Plan et titres", max: 5, attendus: "Plan thématique en deux parties, adapté au sujet précis, équilibré, titres concis et informatifs." },
    { id: "exhaustivite", libelle: "Exhaustivité et exactitude", max: 6, attendus: "Chaque document utilisé sans déformation ; aucune information extérieure au dossier." },
    { id: "croisement", libelle: "Croisement et hiérarchie des sources", max: 4, attendus: "Plusieurs documents par sous-partie, confrontés ; sources hiérarchisées." },
    { id: "expression", libelle: "Expression et format", max: 2, attendus: "Neutralité, quatre pages au plus, style clair, orthographe irréprochable." },
  ],
  champs: {
    sujet: { libelle: "Sujet de la note", aide: "Le titre ou la question posée par le sujet." },
    documents: { libelle: "Dossier documentaire", aide: "Collez chaque document dans son champ, dans l'ordre du dossier." },
  },
};

export const METHODES: Record<TypeExercice, MethodeExercice> = {
  dissertation: DISSERTATION,
  commentaire_arret: COMMENTAIRE_ARRET,
  fiche_arret: FICHE_ARRET,
  commentaire_texte: COMMENTAIRE_TEXTE,
  cas_pratique: CAS_PRATIQUE,
  consultation: CONSULTATION,
  note_synthese: NOTE_SYNTHESE,
  plan_detaille: PLAN_DETAILLE,
};

/** Exercices ouverts dans l'atelier (les autres sont refusés par le serveur). */
export const TYPES_DISPONIBLES: TypeExercice[] = [
  "dissertation",
  "commentaire_arret",
  "commentaire_texte",
  "cas_pratique",
  "consultation",
  "fiche_arret",
  "note_synthese",
  "plan_detaille",
];

/** Étapes de l'introduction, dans l'ordre de la méthode. */
export const ORDRE_INTRO: Record<"dissertation" | "commentaire" | "texte" | "synthese", string[]> = {
  dissertation: ["accroche", "definitions", "delimitation", "contexte", "interets", "problematique", "annonce"],
  commentaire: ["accroche", "presentation", "faits", "procedure", "theses", "probleme", "solution", "interet", "annonce"],
  texte: ["accroche", "presentation", "historique", "objet", "interet", "problematique", "annonce"],
  synthese: ["accroche", "contexte", "problematique", "annonce"],
};

/** Famille d'introduction d'un exercice. */
export function familleIntro(type: TypeExercice): keyof typeof ORDRE_INTRO {
  if (type === "commentaire_arret") return "commentaire";
  if (type === "commentaire_texte") return "texte";
  if (type === "note_synthese") return "synthese";
  return "dissertation";
}

export const LIBELLES_INTRO: Record<string, string> = {
  accroche: "Accroche",
  historique: "Historique du texte",
  objet: "Objet et définitions",
  presentation: "Présentation",
  definitions: "Définitions",
  delimitation: "Délimitation",
  contexte: "Contexte",
  interets: "Intérêts du sujet",
  problematique: "Problématique",
  faits: "Faits",
  procedure: "Procédure",
  theses: "Thèses en présence",
  probleme: "Problème de droit",
  solution: "Solution",
  interet: "Intérêt",
  annonce: "Annonce du plan",
};

export function methodeDe(type: TypeExercice): MethodeExercice {
  return METHODES[type];
}

export function totalBareme(m: MethodeExercice): number {
  return m.bareme.reduce((n, c) => n + c.max, 0);
}
